/**
 * rolesApi.js — Employer v1.1 Phase 0.6 Slice A.
 *
 * Thin `apiFetch` wrappers around the already-live Phase 0.5
 * backend surfaces. Per pre-code plan §13.2 G-I default, this slice
 * uses the project's existing `apiFetch` helper (NOT TanStack Query;
 * the project does not currently depend on it and adding a dep is
 * outside Slice A scope).
 *
 * Slice A consumes READ paths only:
 *   - listRoles()
 *   - getRole(id)
 *   - getRoleCriteria(id)
 *   - getAudit({ roleId? })   ← client-side filter
 *
 * The write helpers (createRole, patchRole, patchCriteria, publish,
 * close, archive) are NOT shipped in Slice A. They land in Slice B/C/D.
 */
import { apiFetch } from '@/lib/api';

export async function listRoles() {
  // Backend response shape: { roles: [...], no_org: bool }.
  const data = await apiFetch('/api/employer/roles');
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.roles)) return data.roles;
  return [];
}

export async function getRole(id) {
  if (!id) {
    throw new Error('rolesApi.getRole: id is required');
  }
  // The legacy /api/employer/roles endpoint returns a list scoped to
  // the caller's org via RLS. The Phase 0.5 single-role read uses the
  // same path with a `?id=eq.{id}` predicate via PostgREST, BUT the
  // existing backend route returns the WHOLE list so we filter
  // client-side. This keeps Slice A purely read-only without adding
  // a backend endpoint.
  const data = await apiFetch('/api/employer/roles');
  const rows = Array.isArray(data)
    ? data
    : (data && Array.isArray(data.roles) ? data.roles : null);
  if (!rows) return null;
  return rows.find((r) => r.id === id) || null;
}

export async function getRoleCriteria(id) {
  if (!id) {
    throw new Error('rolesApi.getRoleCriteria: id is required');
  }
  return apiFetch(`/api/employer/roles/${encodeURIComponent(id)}/criteria`);
}

export async function getAudit({ roleId } = {}) {
  // Phase 0.3 surface; org-scoped via RLS. Slice A filters
  // client-side by role_id per pre-code plan §13.2 G-H default
  // ("client filter to avoid new query param").
  // Backend response shape: { rows: [...], migration_pending: bool }.
  const data = await apiFetch('/api/employer/v1/audit?limit=100');
  const rows = Array.isArray(data)
    ? data
    : (data && Array.isArray(data.rows) ? data.rows : []);
  if (!roleId) return rows;
  return rows.filter((r) => r && r.role_id === roleId);
}

// ─────────────────────────────────────────────────────────────
// Slice B — draft write helpers.
// Calls the legacy POST/PATCH role endpoints + the Phase 0.5
// PATCH /criteria endpoint. NO publish/close/archive helpers
// in Slice B (those land in Slice C/D).
// ─────────────────────────────────────────────────────────────

export async function createRole({ title, structured_definition_json }) {
  return apiFetch('/api/employer/roles', {
    method: 'POST',
    body:   JSON.stringify({
      title,
      structured_definition_json: structured_definition_json || {},
    }),
  });
}

export async function patchRole(id, patch) {
  if (!id) throw new Error('rolesApi.patchRole: id is required');
  return apiFetch(`/api/employer/roles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body:   JSON.stringify(patch),
  });
}

export async function patchCriteria(id, patch) {
  if (!id) throw new Error('rolesApi.patchCriteria: id is required');
  return apiFetch(`/api/employer/roles/${encodeURIComponent(id)}/criteria`, {
    method: 'PATCH',
    body:   JSON.stringify(patch),
  });
}

// ─────────────────────────────────────────────────────────────
// Slice C — publish wrapper.
// Calls the Phase 0.5 atomic publish endpoint. NO close/archive
// helpers in Slice C — those land in Slice D.
// ─────────────────────────────────────────────────────────────

export async function publishRole(id) {
  if (!id) throw new Error('rolesApi.publishRole: id is required');
  return apiFetch(`/api/employer/roles/${encodeURIComponent(id)}/publish`, {
    method: 'POST',
    body:   JSON.stringify({}),
  });
}

// ─────────────────────────────────────────────────────────────
// Slice D — close / archive wrappers.
// Calls the Phase 0.5 atomic close/archive endpoints. The platform
// does not expose any un-archive path; archived is terminal by
// design (W-2).
// ─────────────────────────────────────────────────────────────

export async function closeRole(id, { immediate = false } = {}) {
  if (!id) throw new Error('rolesApi.closeRole: id is required');
  return apiFetch(`/api/employer/roles/${encodeURIComponent(id)}/close`, {
    method: 'POST',
    body:   JSON.stringify({ immediate: Boolean(immediate) }),
  });
}

export async function archiveRole(id) {
  if (!id) throw new Error('rolesApi.archiveRole: id is required');
  return apiFetch(`/api/employer/roles/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    body:   JSON.stringify({}),
  });
}

// ─────────────────────────────────────────────────────────────
// RC 1.62 Phase F — clone-as-new-draft.
// Closed / archived roles are immutable governance records and
// can never be reopened. The sanctioned reuse path is to clone
// their structured definition into a fresh draft with a new id;
// the source role is unaffected. Source role may be any status.
// Backend response shape: { role: <full role projection> }.
// ─────────────────────────────────────────────────────────────
export async function cloneRole(id, { title_override } = {}) {
  if (!id) throw new Error('rolesApi.cloneRole: id is required');
  const body = {};
  if (typeof title_override === 'string' && title_override.trim()) {
    body.title_override = title_override.trim();
  }
  return apiFetch(`/api/employer/roles/${encodeURIComponent(id)}/clone`, {
    method: 'POST',
    body:   JSON.stringify(body),
  });
}
